import { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import { alpha } from '@mui/material/styles';
import ListItemButton from '@mui/material/ListItemButton';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { usePathname } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { useResponsive } from 'src/hooks/use-responsive';

import Logo from 'src/components/logo';
import Scrollbar from 'src/components/scrollbar';

import { NAV } from './config-layout';
import { Icon } from '@iconify/react';
import { common } from 'src/theme/palette';

export default function Nav({ openNav, onCloseNav, isCollapsed = false, onToggleCollapse }) {
  const pathname = usePathname();
  const location = useLocation();
  const lgUp = useResponsive('up', 'lg');
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const [isLanguageReady, setIsLanguageReady] = useState(false);
  const [cookies] = useCookies(['user']);
  const userRole = cookies?.user?.role;
  const prevPathnameRef = useRef(pathname);

  const syncLanguage = useCallback(async () => {
    if (lang && i18n.isInitialized) {
      if (lang !== i18n.language) {
        try {
          await i18n.changeLanguage(lang);
          localStorage.setItem('i18nextLng', lang);
        } catch (error) {
          console.error('Error changing language:', error);
        }
      }
      setIsLanguageReady(true);
    }
  }, [lang, i18n]);

  const handleToggleCollapse = () => {
    onToggleCollapse();
  };

  const navSections = useMemo(() => {
    if (!isLanguageReady || !lang) return [];

    const allSections = [
      {
        label: t('navigation.overview', 'Overview'),
        items: [
          {
            key: 'dashboard',
            title: t('navigation.dashboard'),
            path: `/${lang}/dashboard`,
            icon: <Icon icon="solar:home-smile-bold-duotone" width="20" height="20" />,
          },
        ],
      },
      {
        label: t('navigation.operations', 'Operations'),
        items: [
          {
            key: 'depo',
            title: t('navigation.depo'),
            path: `/${lang}/depo`,
            icon: <Icon icon="solar:buildings-bold-duotone" width="20" height="20" />,
          },
          {
            key: 'carriages',
            title: t('navigation.carriages'),
            path: `/${lang}/carriages`,
            icon: <Icon icon="solar:bus-bold-duotone" width="20" height="20" />,
          },
          {
            key: 'history',
            title: t('navigation.history'),
            path: `/${lang}/history`,
            icon: <Icon icon="solar:history-bold-duotone" width="20" height="20" />,
          },
          {
            key: 'direction-plan',
            title: t('navigation.directionPlan'),
            path: `/${lang}/direction-plan`,
            icon: <Icon icon="solar:calendar-mark-bold-duotone" width="20" height="20" />,
          },
          {
            key: 'train-records',
            title: t('navigation.trainRecords', 'Poyezd yozuvlari'),
            path: `/${lang}/train-records`,
            icon: <Icon icon="solar:siren-rounded-bold-duotone" width="20" height="20" />,
          },
        ],
      },
      {
        label: t('navigation.management', 'Management'),
        items: [
          {
            key: 'inspections',
            title: t('navigation.inspections'),
            path: `/${lang}/inspections`,
            icon: <Icon icon="solar:clipboard-check-bold-duotone" width="20" height="20" />,
          },
          {
            key: 'inspection-schedule',
            title: t('navigation.inspectionSchedule'),
            path: `/${lang}/inspection-schedule`,
            icon: <Icon icon="solar:list-check-bold-duotone" width="20" height="20" />,
          },
          {
            key: 'inspection-timeline',
            title: t('navigation.inspectionTimeline'),
            path: `/${lang}/inspection-timeline`,
            icon: <Icon icon="solar:sort-by-time-outline" width="20" height="20" />,
          },
          {
            key: 'problems',
            title: t('navigation.problems'),
            path: `/${lang}/problems`,
            icon: <Icon icon="solar:danger-bold-duotone" width="20" height="20" />,
          },
        ],
      },
      {
        label: t('navigation.admin', 'Admin'),
        items: [
          {
            key: 'users',
            title: t('navigation.users'),
            path: `/${lang}/users`,
            icon: <Icon icon="solar:users-group-rounded-bold-duotone" width="20" height="20" />,
          },
          {
            key: 'organizations',
            title: t('navigation.organizations', 'Tashkilotlar'),
            path: `/${lang}/organizations`,
            icon: <Icon icon="solar:buildings-2-bold-duotone" width="20" height="20" />,
          },
        ],
      },
      {
        label: t('navigation.tools', 'Tools'),
        items: [
          {
            key: 'map',
            title: t('navigation.map', 'Map'),
            path: `/${lang}/map`,
            icon: <Icon icon="solar:map-bold-duotone" width="20" height="20" />,
          },
          {
            key: 'smartdepo',
            title: t('navigation.smartdepo'),
            path: `https://smartdepo.uz`,
            icon: <Icon icon="solar:link-bold-duotone" width="20" height="20" />,
          },
          {
            key: 'activity-logs',
            title: t('navigation.activityLogs'),
            path: `/${lang}/activity-logs`,
            icon: <Icon icon="solar:clock-circle-bold-duotone" width="20" height="20" />,
          },
        ],
      },
    ];

    const getAllowedKeys = (role) => {
      if (role === 'dispatcher')
        return ['dashboard', 'depo', 'history', 'carriages', 'direction-plan', 'map'];
      if (role === 'texnolog') return ['problems', 'dashboard'];
      if (role === 'bort_injener') return ['train-records'];
      if (role === 'admin') return allSections.flatMap((s) => s.items.map((i) => i.key));
      // all other roles: exclude admin-only / role-specific pages
      return allSections
        .flatMap((s) => s.items.map((i) => i.key))
        .filter((k) => !['users', 'organizations', 'train-records'].includes(k));
    };

    const allowedKeys = getAllowedKeys(userRole);

    return allSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => allowedKeys.includes(item.key)),
      }))
      .filter((section) => section.items.length > 0);
  }, [t, lang, isLanguageReady, userRole]);

  useEffect(() => {
    if (openNav && pathname !== prevPathnameRef.current) {
      onCloseNav();
    }
    prevPathnameRef.current = pathname;
  }, [pathname, openNav, onCloseNav]);

  useEffect(() => {
    syncLanguage();
  }, [syncLanguage]);

  useEffect(() => {
    const initializeLanguage = async () => {
      if (lang && i18n.isInitialized) {
        if (!i18n.language || i18n.language !== lang) {
          await syncLanguage();
        } else {
          setIsLanguageReady(true);
        }
      }
    };
    initializeLanguage();
  }, [lang, i18n, syncLanguage]);

  useEffect(() => {
    if (lang && i18n.language === lang && !isLanguageReady) {
      setIsLanguageReady(true);
    }
  }, [lang, i18n.language, isLanguageReady]);

  const renderMenu = (
    <Stack
      component="nav"
      spacing={0}
      sx={{ px: lgUp && isCollapsed ? 1 : 2, transition: 'all 0.3s ease-in-out' }}
    >
      {navSections.map((section, idx) => (
        <Box key={section.label}>
          {!isCollapsed ? (
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                px: 1,
                pt: idx === 0 ? 0.5 : 2.5,
                pb: 0.75,
                color: 'text.disabled',
                fontSize: '0.625rem',
                fontWeight: 700,
                letterSpacing: '1.2px',
              }}
            >
              {section.label}
            </Typography>
          ) : (
            idx > 0 && (
              <Box
                sx={{
                  my: 1.5,
                  mx: 'auto',
                  width: 24,
                  height: '1px',
                  bgcolor: 'divider',
                }}
              />
            )
          )}
          <Stack spacing={0.5}>
            {section.items.map((item) => (
              <NavItem
                key={item.path}
                item={item}
                active={location.pathname === item.path}
                isCollapsed={lgUp ? isCollapsed : false}
              />
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: lgUp && isCollapsed ? 'center' : 'space-between',
          mt: 3,
          mb: 2,
          px: lgUp && isCollapsed ? 1 : 3,
          transition: 'all 0.3s ease-in-out',
        }}
      >
        {lgUp && isCollapsed ? (
          <IconButton
            onClick={handleToggleCollapse}
            sx={{
              color: 'text.secondary',
              transition: 'all 0.3s ease-in-out',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Icon icon="eva:arrow-ios-forward-fill" width="20" height="20" />
          </IconButton>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Logo />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  fontSize: '1.1rem',
                  transition: 'opacity 0.3s ease-in-out',
                }}
              >
                Smart Carriage
              </Typography>
            </Box>

            {lgUp && (
              <IconButton
                onClick={handleToggleCollapse}
                sx={{
                  color: 'text.secondary',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Icon icon="eva:arrow-ios-back-fill" width="20" height="20" />
              </IconButton>
            )}
          </>
        )}
      </Box>

      {renderMenu}

      {lgUp && isCollapsed && (
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'flex-end', pb: 2 }}>
          <IconButton
            onClick={handleToggleCollapse}
            sx={{
              color: 'text.secondary',
              mx: 'auto',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Icon icon="eva:arrow-ios-forward-fill" width="20" height="20" />
          </IconButton>
        </Box>
      )}
    </Scrollbar>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: isCollapsed ? 80 : NAV.WIDTH },
        background: common.white,
        transition: 'width 0.3s ease-in-out',
      }}
    >
      {lgUp ? (
        <Box
          sx={{
            height: 1,
            position: 'fixed',
            width: isCollapsed ? 80 : NAV.WIDTH,
            borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
            transition: 'width 0.3s ease-in-out',
          }}
        >
          {renderContent}
        </Box>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          variant="temporary"
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: NAV.WIDTH,
              zIndex: (theme) => theme.zIndex.drawer + 1,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
  isCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
};

function NavItem({ item, active, isCollapsed }) {
  const navItem = (
    <ListItemButton
      component={RouterLink}
      href={item.path}
      sx={{
        borderRadius: 1.5,
        color: active ? 'primary.main' : 'text.secondary',
        fontSize: '0.875rem',
        fontWeight: active ? 600 : 500,
        minHeight: 44,
        px: isCollapsed ? 0.5 : 1.5,
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        transition: 'all 0.2s ease-in-out',

        ...(active && {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
          },
        }),

        ...(!active && {
          '&:hover': {
            color: 'text.primary',
            bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
          },
        }),
      }}
    >
      <Box
        component="span"
        sx={{
          width: 36,
          height: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 1,
          flexShrink: 0,
          mr: isCollapsed ? 0 : 1.5,
          bgcolor: active
            ? (theme) => alpha(theme.palette.primary.main, 0.12)
            : (theme) => alpha(theme.palette.grey[500], 0.08),
          color: active ? 'primary.main' : 'text.secondary',
          transition: 'all 0.2s ease-in-out',
        }}
      >
        {item.icon}
      </Box>

      {!isCollapsed && (
        <Box
          component="span"
          sx={{ flexGrow: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {item.title}
        </Box>
      )}
    </ListItemButton>
  );

  if (isCollapsed) {
    return (
      <Tooltip title={item.title} placement="right" arrow>
        {navItem}
      </Tooltip>
    );
  }

  return navItem;
}

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.bool,
  isCollapsed: PropTypes.bool,
};
